import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

type FormAccess<TFieldValues extends Record<string, any>> = Pick<
  UseFormReturn<TFieldValues>,
  'watch' | 'getValues'
>;

/**
 * Debounced read of a single form field that does NOT re-render on every change.
 *
 * `useDebounce(watch('field'), delay)` still subscribes the host component to the
 * raw field, so a slider drag re-renders the whole host on every step and only the
 * derived value is delayed. This hook subscribes through the callback form of
 * `watch`, which notifies without rendering, so the host re-renders only when the
 * debounced value settles.
 *
 * Intended for fields driven by continuous input (sliders) that feed expensive
 * downstream work.
 */
export function useDebouncedFormValue<
  TFieldValues extends Record<string, any>,
  TValue = unknown,
>(
  { watch, getValues }: FormAccess<TFieldValues>,
  name: keyof TFieldValues & string,
  delay = 200,
): TValue {
  const [settledValue, setSettledValue] = useState<TValue>(
    () => getValues(name as any) as TValue,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const subscription = watch((_values, { name: changedName }) => {
      if (changedName !== undefined && changedName !== name) {
        return;
      }

      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        // Read through getValues rather than the notified snapshot: the snapshot is a
        // clone, so object-valued fields would get a fresh identity on every settle
        // and defeat downstream memoisation.
        setSettledValue(getValues(name as any) as TValue);
      }, delay);
    });

    return () => {
      subscription.unsubscribe();
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [watch, getValues, name, delay]);

  return settledValue;
}
